import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { PortalShell } from "../components/PortalShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ApiError } from "../lib/api";
import {
  TEAM_ROLES,
  inviteMember,
  listTeam,
  revokeInvitation,
  type InviteResult,
  type PendingInvitation,
  type TeamMember,
  type TeamRole,
} from "../lib/team";
import { useAuthStore } from "../stores/auth";

export function TeamPage(): JSX.Element {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === "admin";

  const { data, isLoading, error } = useQuery({
    queryKey: ["team"],
    queryFn: listTeam,
  });

  const [showForm, setShowForm] = useState(false);
  const [lastCreated, setLastCreated] = useState<InviteResult | null>(null);

  const revokeMutation = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team"] }),
  });

  const members = data?.members ?? [];
  const invitations = data?.pending_invitations ?? [];

  return (
    <PortalShell>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Team</h1>
          <p className="text-sm text-neutral-600">
            Workspace members and pending invitations.
          </p>
        </div>
        {isAdmin && !showForm && lastCreated === null && (
          <Button variant="primary" onClick={() => setShowForm(true)}>
            + Invite member
          </Button>
        )}
      </header>

      {lastCreated !== null && (
        <InvitationCreatedBanner
          invitation={lastCreated}
          onClose={() => {
            setLastCreated(null);
            setShowForm(false);
            void queryClient.invalidateQueries({ queryKey: ["team"] });
          }}
        />
      )}

      {showForm && lastCreated === null && (
        <InviteForm
          onCreated={(inv) => setLastCreated(inv)}
          onCancel={() => setShowForm(false)}
        />
      )}

      <section className="mt-6 rounded-lg border border-neutral-200 bg-white shadow-sm">
        <header className="border-b border-neutral-200 px-6 py-3 text-sm font-semibold text-neutral-700">
          Members ({members.length})
        </header>
        {isLoading ? (
          <p className="px-6 py-12 text-sm text-neutral-500">Loading…</p>
        ) : error instanceof ApiError ? (
          <p
            role="alert"
            className="m-6 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
          >
            {error.problem.detail || error.problem.title}
          </p>
        ) : members.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-neutral-500">
            No members yet.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {members.map((member) => (
              <MemberRow key={member.id} member={member} isCurrentUser={member.id === user?.id} />
            ))}
          </ul>
        )}
      </section>

      {invitations.length > 0 && (
        <section className="mt-6 rounded-lg border border-neutral-200 bg-white shadow-sm">
          <header className="border-b border-neutral-200 px-6 py-3 text-sm font-semibold text-neutral-700">
            Pending invitations ({invitations.length})
          </header>
          <ul className="divide-y divide-neutral-200">
            {invitations.map((inv) => (
              <InvitationRow
                key={inv.id}
                invitation={inv}
                isAdmin={isAdmin}
                onRevoke={() => revokeMutation.mutate(inv.id)}
                revoking={revokeMutation.isPending && revokeMutation.variables === inv.id}
              />
            ))}
          </ul>
        </section>
      )}
    </PortalShell>
  );
}

function MemberRow({
  member,
  isCurrentUser,
}: {
  member: TeamMember;
  isCurrentUser: boolean;
}): JSX.Element {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">
          {member.name ?? member.email}
          {isCurrentUser && <span className="ml-2 text-xs text-neutral-500">(you)</span>}
        </p>
        <p className="truncate text-xs text-neutral-500">{member.email}</p>
        <p className="mt-1 text-xs text-neutral-500">
          {member.email_verified ? "Verified" : "Unverified"}
          {member.last_login_at !== null && <> · Last login {formatDate(member.last_login_at)}</>}
        </p>
      </div>
      <RoleBadge role={member.role} />
    </li>
  );
}

function InvitationRow({
  invitation,
  isAdmin,
  onRevoke,
  revoking,
}: {
  invitation: PendingInvitation;
  isAdmin: boolean;
  onRevoke: () => void;
  revoking: boolean;
}): JSX.Element {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-neutral-900">
          {invitation.email}
        </p>
        <p className="text-xs text-neutral-500">
          Expires {formatDate(invitation.expires_at)}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <RoleBadge role={invitation.role} />
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            loading={revoking}
            onClick={() => {
              if (window.confirm(`Revoke invitation for ${invitation.email}?`)) {
                onRevoke();
              }
            }}
          >
            Revoke
          </Button>
        )}
      </div>
    </li>
  );
}

function RoleBadge({ role }: { role: TeamRole }): JSX.Element {
  const palette: Record<TeamRole, string> = {
    admin: "bg-primary-50 text-primary-700",
    editor: "bg-success-50 text-success-700",
    viewer: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${palette[role]}`}
    >
      {role}
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function InviteForm({
  onCreated,
  onCancel,
}: {
  onCreated: (inv: InviteResult) => void;
  onCancel: () => void;
}): JSX.Element {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("editor");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: inviteMember,
    onSuccess: onCreated,
    onError: (err) => {
      setError(err instanceof ApiError ? err.problem.detail || err.problem.title : "Couldn't invite.");
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    mutation.mutate({ email, role });
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-neutral-900">Invite a member</h2>
      <Input
        label="Email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div className="flex flex-col gap-1.5">
        <label htmlFor="invite-role" className="text-sm font-medium text-neutral-700">
          Role
        </label>
        <select
          id="invite-role"
          value={role}
          onChange={(e) => setRole(e.target.value as TeamRole)}
          className="h-10 rounded-md border border-neutral-300 bg-white px-3 text-sm text-neutral-900"
        >
          {TEAM_ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>
      {error !== null && (
        <p
          role="alert"
          className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
        >
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" loading={mutation.isPending}>
          Send invitation
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function InvitationCreatedBanner({
  invitation,
  onClose,
}: {
  invitation: InviteResult;
  onClose: () => void;
}): JSX.Element {
  const acceptUrl =
    invitation.invitation_token !== null && invitation.invitation_token !== undefined
      ? `${window.location.origin}/accept-invitation?token=${encodeURIComponent(invitation.invitation_token)}`
      : null;
  return (
    <div className="rounded-lg border border-success-200 bg-success-50 p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-success-700">Invitation sent</h2>
      <p className="mt-1 text-sm text-success-700">
        We&apos;ve emailed {invitation.email} a link to join as {invitation.role}.
      </p>
      {acceptUrl !== null && (
        <p className="mt-3 break-all rounded border border-neutral-300 bg-white p-3 font-mono text-xs text-neutral-700">
          Dev mode: {acceptUrl}
        </p>
      )}
      <div className="mt-4">
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  );
}
